import type { ProformaStatus } from '@horevia/contracts';
import {
  Avatar,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  ChipFilter,
  Container,
  EmptyState,
  ErrorState,
  Input,
  KpiCard,
  NoPermissionState,
  PageHeader,
  Table,
  TableSkeleton,
  TableWrap,
  Td,
  Th,
  Toolbar,
  Tr,
  UserCell,
} from '@horevia/ui';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { Ban, CheckCircle2, Clock, FileText, List, Plus, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Can, useCan } from '@/features/auth/session';
import { ProformaFormDrawer } from '@/features/billing/ProformaFormDrawer';
import { billingSettingsQueryOptions, proformasQueryOptions } from '@/features/billing/queries';
import { PROFORMA_STATUSES, daysUntil, proformaTone } from '@/features/billing/status';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatAmount, formatDateShort } from '@/lib/format';
import { useQuery } from '@tanstack/react-query';

interface ProformasSearch {
  q?: string;
  status?: string;
  new?: string;
}

export const Route = createFileRoute('/_app/billing/proformas/')({
  validateSearch: (search: Record<string, unknown>): ProformasSearch => ({
    ...(typeof search['q'] === 'string' ? { q: search['q'] } : {}),
    ...(typeof search['status'] === 'string' ? { status: search['status'] } : {}),
    ...(typeof search['new'] === 'string' ? { new: search['new'] } : {}),
  }),
  component: ProformasPage,
});

function ProformasPage() {
  const { t, i18n } = useTranslation(['billing', 'common', 'errors']);
  const search = Route.useSearch();
  const navigate = useNavigate();
  const canRead = useCan('proforma.read');

  const [term, setTerm] = useState(search.q ?? '');
  const [debounced, setDebounced] = useState(search.q ?? '');
  const [status, setStatus] = useState<ProformaStatus | undefined>(
    PROFORMA_STATUSES.includes(search.status as ProformaStatus)
      ? (search.status as ProformaStatus)
      : undefined,
  );
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [createOpen, setCreateOpen] = useState(search.new === 'proforma');

  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(term.length >= 2 ? term : ''), 250);
    return () => window.clearTimeout(timer);
  }, [term]);

  const filters = {
    ...(debounced ? { q: debounced } : {}),
    ...(status ? { status } : {}),
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
  };

  const query = useInfiniteQuery(proformasQueryOptions(filters, 25, canRead));
  const settings = useQuery(billingSettingsQueryOptions(canRead));
  const rows = useMemo(() => query.data?.pages.flatMap((page) => page.data) ?? [], [query.data]);

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  const issued = rows.filter((row) => row.status !== 'DRAFT' && row.status !== 'CANCELLED');
  const accepted = rows.filter((row) => row.status === 'ACCEPTED');
  const pending = rows.filter((row) => row.status === 'ISSUED');
  const expired = rows.filter((row) => row.status === 'EXPIRED');
  const issuedAmount = issued.reduce((sum, row) => sum + row.totals.netTotal, 0);
  const conversionRate =
    issued.length > 0 ? Math.round((accepted.length / issued.length) * 100) : 0;
  const hasFilters = debounced !== '' || status !== undefined || from !== '' || to !== '';

  return (
    <Container>
      <PageHeader
        title={t('billing:proformas.title')}
        subtitle={t('billing:proformas.subtitle', {
          days: settings.data?.proformaValidityDays ?? 15,
        })}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('billing:proformas.title') },
        ]}
        actions={
          <Can permission="proforma.create">
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              <Plus className="ico" aria-hidden="true" />
              {t('billing:proformas.create')}
            </Button>
          </Can>
        }
      />

      <div className="stack">
        <div className="grid grid-4">
          <KpiCard
            tone="blue"
            icon={<FileText className="ico ico-16" aria-hidden="true" />}
            label={t('billing:proformas.kpi.issued')}
            value={issued.length}
            loading={query.isPending}
            footer={t('billing:proformas.kpi.issuedFoot', {
              amount: formatAmount(issuedAmount, i18n.language),
            })}
          />
          <KpiCard
            tone="green"
            icon={<CheckCircle2 className="ico ico-16" aria-hidden="true" />}
            label={t('billing:proformas.kpi.accepted')}
            value={accepted.length}
            loading={query.isPending}
            footer={t('billing:proformas.kpi.acceptedFoot', { rate: conversionRate })}
          />
          <KpiCard
            tone="amber"
            icon={<Clock className="ico ico-16" aria-hidden="true" />}
            label={t('billing:proformas.kpi.pending')}
            value={pending.length}
            loading={query.isPending}
            footer={t('billing:proformas.kpi.pendingFoot')}
          />
          <KpiCard
            tone="red"
            icon={<Ban className="ico ico-16" aria-hidden="true" />}
            label={t('billing:proformas.kpi.expired')}
            value={expired.length}
            loading={query.isPending}
            footer={t('billing:proformas.kpi.expiredFoot')}
          />
        </div>

        <Card>
          <CardHeader
            title={t('billing:proformas.listTitle')}
            icon={<List className="ico ico-16" aria-hidden="true" />}
            iconTone="blue"
            actions={
              <Badge tone="neutral">
                {t('billing:proformas.countLabel', { count: rows.length })}
              </Badge>
            }
          />

          <Toolbar>
            <Input
              type="search"
              value={term}
              onChange={(event) => setTerm(event.target.value)}
              placeholder={t('billing:proformas.searchPlaceholder')}
              aria-label={t('billing:proformas.searchLabel')}
              icon={<Search className="ico" aria-hidden="true" />}
              style={{ width: 260, height: 32, fontSize: 'var(--fs-13)' }}
            />
            {PROFORMA_STATUSES.map((value) => (
              <ChipFilter
                key={value}
                active={status === value}
                onClick={() => setStatus((current) => (current === value ? undefined : value))}
              >
                {t(`billing:status.proforma.${value}`)}
              </ChipFilter>
            ))}
          </Toolbar>

          <Toolbar>
            <Input
              type="date"
              aria-label={t('billing:invoices.filters.from')}
              value={from}
              onChange={(event) => setFrom(event.target.value)}
              style={{ width: 170, height: 30, fontSize: 'var(--fs-13)' }}
            />
            <Input
              type="date"
              aria-label={t('billing:invoices.filters.to')}
              value={to}
              onChange={(event) => setTo(event.target.value)}
              style={{ width: 170, height: 30, fontSize: 'var(--fs-13)' }}
            />
            <span className="spacer" />
            {hasFilters ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setTerm('');
                  setDebounced('');
                  setStatus(undefined);
                  setFrom('');
                  setTo('');
                }}
              >
                {t('common:actions.clearFilters')}
              </Button>
            ) : null}
          </Toolbar>

          <CardBody density="flush">
            {query.isPending ? (
              <TableSkeleton rows={6} columns={7} label={t('common:states.loading')} />
            ) : query.isError ? (
              <ErrorState
                title={t('common:states.errorTitle')}
                description={errorMessage(query.error, t)}
                traceId={errorTraceId(query.error)}
                retryLabel={t('common:actions.retry')}
                onRetry={() => void query.refetch()}
                traceCopyLabel={t('common:actions.copy')}
                traceCopiedLabel={t('common:actions.copied')}
              />
            ) : rows.length === 0 ? (
              <EmptyState
                icon={<FileText className="ico ico-24" aria-hidden="true" />}
                title={
                  hasFilters
                    ? t('billing:proformas.emptySearchTitle')
                    : t('billing:proformas.emptyTitle')
                }
                description={
                  hasFilters
                    ? t('billing:proformas.emptySearchBody')
                    : t('billing:proformas.emptyBody')
                }
              />
            ) : (
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <Th>{t('billing:proformas.columns.number')}</Th>
                      <Th>{t('billing:proformas.columns.billingParty')}</Th>
                      <Th>{t('billing:proformas.columns.reservation')}</Th>
                      <Th>{t('billing:proformas.columns.issueDate')}</Th>
                      <Th>{t('billing:proformas.columns.validUntil')}</Th>
                      <Th align="right">{t('billing:proformas.columns.net')}</Th>
                      <Th>{t('billing:proformas.columns.status')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const remaining = daysUntil(row.validUntil);
                      return (
                        <Tr key={row.id}>
                          <Td>
                            <Link
                              to="/billing/proformas/$proformaId"
                              params={{ proformaId: row.id }}
                            >
                              <span className="cell-id">
                                {row.number ?? t('billing:status.proforma.DRAFT')}
                              </span>
                            </Link>
                          </Td>
                          <Td>
                            <UserCell
                              avatar={<Avatar name={row.billingParty.displayName} size="sm" />}
                              name={row.billingParty.displayName}
                            />
                          </Td>
                          <Td className="t-13">{row.reservationReference ?? '—'}</Td>
                          <Td className="t-13" numeric>
                            {row.issueDate ? formatDateShort(row.issueDate, i18n.language) : '—'}
                          </Td>
                          <Td className="t-13" numeric>
                            {row.validUntil ? formatDateShort(row.validUntil, i18n.language) : '—'}
                          </Td>
                          <Td align="right" numeric>
                            {formatAmount(row.totals.netTotal, i18n.language)}
                          </Td>
                          <Td>
                            <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                              <Badge tone={proformaTone(row.status)} dot>
                                {t(`billing:status.proforma.${row.status}`)}
                              </Badge>
                              {row.convertedInvoiceId ? (
                                <Badge tone="violet">{t('billing:status.converted')}</Badge>
                              ) : null}
                              {row.status === 'ISSUED' && remaining !== null && remaining >= 0 ? (
                                <Badge tone={remaining <= 3 ? 'red' : 'amber'}>
                                  {remaining === 0
                                    ? t('billing:status.expiresToday')
                                    : t('billing:status.expiresIn', { count: remaining })}
                                </Badge>
                              ) : null}
                            </div>
                          </Td>
                        </Tr>
                      );
                    })}
                  </tbody>
                </Table>
              </TableWrap>
            )}
          </CardBody>

          {query.hasNextPage ? (
            <div className="card__foot">
              <Button
                variant="secondary"
                size="sm"
                loading={query.isFetchingNextPage}
                onClick={() => void query.fetchNextPage()}
              >
                {t('common:pagination.loadMore')}
              </Button>
            </div>
          ) : null}
        </Card>
      </div>

      <ProformaFormDrawer
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(proforma) =>
          void navigate({
            to: '/billing/proformas/$proformaId',
            params: { proformaId: proforma.id },
          })
        }
      />
    </Container>
  );
}
